import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Understanding Balanced Filipino Plate: Your Guide to a Balanced Plate',
    content: [
      '<strong>Deep Dive:</strong> Balanced Filipino Plate is more than just a meal guide–it\'s a visual tool that shows you exactly how to portion your plate for optimal nutrition. This simple but powerful approach divides your plate into sections that ensure you get the right balance of nutrients at every meal.'
    ],
    additionalSection: {
      title: 'Key Concepts:',
      content: [
        '<strong>Half Your Plate:</strong> Fruits and vegetables (Glow Foods) - providing essential vitamins, minerals, and fiber.',
        '<strong>One Quarter Each:</strong> Rice/alternatives (Go Foods) and protein-rich foods (Grow Foods) - balancing energy and tissue repair.',
        '<strong>Complete the Meal:</strong> Water or milk as your beverage of choice.'
      ]
    },
    additionalSection2: {
      title: 'Key Takeaway',
      content: [
        'The Balanced Filipino Plate model simplifies healthy eating by providing clear, visual guidelines for portion control and food group balance.'
      ]
    }
  },
  {
    title: 'Benefits of Following Balanced Filipino Plate',
    content: [
      '<strong>• Balanced Nutrition:</strong> By following the Balanced Filipino Plate proportions, you naturally achieve a balance of carbohydrates, proteins, vitamins, and minerals.',
      '<strong>• Portion Control:</strong> The visual guide helps prevent overeating while ensuring you get enough of each food group.',
      '<strong>• Cultural Relevance:</strong> This model is specifically designed for Filipino eating patterns, making it practical and easy to follow with local ingredients and dishes.'
    ],
    additionalSection: {
      title: 'Practical Tips',
      content: [
        '<strong>1. Start Small:</strong> Begin by adjusting one meal at a time to match the Balanced Filipino Plate proportions.',
        '<strong>2. Be Flexible:</strong> The model can be adapted to different meal situations while maintaining the basic proportions.',
        '<strong>3. Listen to Your Body:</strong> Use these proportions as a guide while paying attention to your hunger and fullness cues.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s see how well you understand the Balanced Filipino Plate model and its application in daily life.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your understanding of the Balanced Filipino Plate model.',
        'The quiz will help you review key concepts about portion sizes, food group balance, and practical application of the guidelines.'
      ]
    }
  }
];

const NutritionalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('nutritionalM2');
  const totalSteps = moduleContent.length;
  
  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Only save completion status when finishing the last content step
      await saveProgress(nextStep, nextStep === totalSteps - 1);
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/nutritional-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    // Don't change completion status when navigating back
    await saveProgress(prevStep, isCompleted);
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  // Only show completion message on the last step
  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Error boundary for undefined module
  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        <div 
          className="module-left nutritional-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Building a Balanced Filipino Plate</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn about the Balanced Filipino Plate model, a simple but powerful guide to creating balanced, nutritious meals using Filipino ingredients and portions.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentStep === 0 ? handleExit : handleBack}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {currentStep < totalSteps - 1 ? (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/nutritional-module-2/quiz')}
              >
                Take the Quiz
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule2Page;

